import L from "leaflet";
import type { DrawApi } from "../core/api";
import type { DrawMode, LatLngTuple } from "../core/options";
import type { DrawGeometry, DrawSnapshot } from "../core/store";
import { isEditableTarget } from "../core/support";
import { simplifyPoints } from "../geo/simplify";
import type { SnapCandidate } from "../geo/snap";
import { geometryPoints, parseWkt, ringPoints } from "../geo/wkt";
import { zIndexBands } from "../sources/types";
import { History } from "./History";
import type { DrawHost, DrawPhase, Tool, ToolHost } from "./host";
import { LineTool } from "./LineTool";
import { mergeLines, reversePoints, splitLine } from "./LineOps";
import { measureGeometry } from "./Measure";
import { PointTool } from "./PointTool";
import { PolygonTool } from "./PolygonTool";
import { Snapper } from "./Snapper";

export const drawPane = "fxmap-draw";

export type DrawStartOptions = { wkt?: string | null; points?: LatLngTuple[]; editable?: boolean; fit?: boolean };

export type DrawSession = DrawApi & {
  dispose(): void;
  addSnapSource(source: () => SnapCandidate[]): () => void;
  active(): boolean;
  phase(): DrawPhase | null;
  editable(): boolean;
  canUndo(): boolean;
  canRedo(): boolean;
  snap(): boolean;
  lastSplitRemainder(): LatLngTuple[] | null;
  previewPoints(points: LatLngTuple[]): void;
  commitPreview(): void;
  selectedVertex(): number | null;
  segment(): { latlng: LatLngTuple; meters: number } | null;
  handleKey(event: KeyboardEvent): boolean;
};

export function pointsFromWkt(wkt: string | null | undefined, mode: DrawMode): LatLngTuple[] {
  const geometry = parseWkt(wkt);

  if (geometry === null) return [];
  if (mode === "polygon" && geometry.kind === "polygon") return ringPoints(geometry.rings[0]);
  if (mode === "point") return geometryPoints(geometry).slice(0, 1);

  return geometryPoints(geometry);
}

const modeKeys: Record<string, DrawMode> = { "1": "point", "2": "polyline", "3": "polygon" };

export function createDrawSession(host: DrawHost): DrawSession {
  const { map, labels, store, events } = host;

  if (!map.getPane(drawPane)) {
    const pane = map.createPane(drawPane);

    pane.style.zIndex = String(zIndexBands.draw);
  }

  const renderer = L.svg({ pane: drawPane });
  const snapper = new Snapper(map, drawPane, host.options.snap, host.reducedMotion, () => host.snapCandidates());
  const history = new History<LatLngTuple[]>([]);

  let mode: DrawMode | null = null;
  let phase: DrawPhase = "drawing";
  let points: LatLngTuple[] = [];
  let editable = host.options.editable;
  let tool: Tool | null = null;
  let splitRemainder: LatLngTuple[] | null = null;
  let previewActive = false;
  let doubleClickZoomWasEnabled = false;
  let selectedVertex: number | null = null;
  let segment: { latlng: LatLngTuple; meters: number } | null = null;
  let disposed = false;

  const geometry = (): DrawGeometry => measureGeometry(mode, points, labels);

  const snapshot = (): DrawSnapshot => ({
    geometry: geometry(),
    phase: mode === null ? "idle" : phase,
    canUndo: history.canUndo(),
    canRedo: history.canRedo(),
    snap: snapper.enabled,
    editable
  });

  const syncStore = () => {
    if (!disposed) store.setState({ draw: snapshot() });
  };

  const render = () => {
    const current = geometry();

    tool?.render(points, current.valid || points.length === 0);
    map.getContainer().classList.toggle("fxmap-cursor-crosshair", mode !== null && editable && (phase === "drawing" || mode === "polyline"));
    syncStore();
  };

  const emitChange = () => {
    events.emit("draw:change", geometry());
  };

  const setPoints = (next: LatLngTuple[], record: boolean) => {
    points = next.map(a => [a[0], a[1]]);

    if (selectedVertex !== null && selectedVertex >= points.length) selectedVertex = null;
    if (record) history.push(points);

    render();
    emitChange();
  };

  const emitMode = () => {
    events.emit("draw:mode", { mode });
    syncStore();
  };

  const setPhase = (next: DrawPhase) => {
    if (phase === next) return;

    phase = next;
    render();
  };

  const removeTool = () => {
    tool?.dispose();
    tool = null;
    segment = null;
    snapper.clearFlash();
    map.getContainer().classList.remove("fxmap-cursor-crosshair");

    if (doubleClickZoomWasEnabled) {
      map.doubleClickZoom.enable();
      doubleClickZoomWasEnabled = false;
    }
  };

  const toolHost: ToolHost = {
    host,
    pane: drawPane,
    renderer,
    snapper,
    points: () => points,
    editable: () => editable,
    phase: () => phase,
    setPhase,
    commit: next => {
      previewActive = false;
      setPoints(next, true);
    },
    preview: next => {
      previewActive = true;
      points = next.map(a => [a[0], a[1]]);
      render();
      emitChange();
    },
    replaceWithoutHistory: next => {
      points = next.map(a => [a[0], a[1]]);
      history.reset(points);
      render();
    },
    finish: () => finish(),
    cancel: () => cancel(),
    splitAt: (segmentIndex, point) => {
      const result = splitLine(points, segmentIndex, point, "start");

      splitRemainder = result.remainder.length >= 2 ? result.remainder : null;
      setPoints(result.kept, true);
    },
    selectVertex: index => {
      selectedVertex = index;
    },
    selectedVertex: () => selectedVertex,
    showSegment: (latlng, meters) => {
      segment = { latlng, meters };
    },
    hideSegment: () => {
      segment = null;
    }
  };

  const createTool = (next: DrawMode): Tool => {
    switch (next) {
      case "point":
        return new PointTool(toolHost);
      case "polyline":
        return new LineTool(toolHost);
      case "polygon":
        return new PolygonTool(toolHost);
    }
  };

  const finish = () => {
    if (mode === null) return;

    const current = geometry();

    if (!current.valid) return;

    tool?.finishGesture();
    phase = "editing";
    render();
    events.emit("draw:finish", current);
  };

  const cancel = () => {
    if (mode === null) return;

    const cancelled = mode;

    removeTool();
    mode = null;
    points = [];
    selectedVertex = null;
    history.reset([]);
    splitRemainder = null;
    events.emit("draw:cancel", { mode: cancelled });
    emitMode();
  };

  const start = (next: DrawMode, options: DrawStartOptions = {}) => {
    removeTool();
    mode = next;
    editable = options.editable ?? host.options.editable;
    splitRemainder = null;
    selectedVertex = null;

    const initial = options.points !== undefined ? options.points : pointsFromWkt(options.wkt, next);

    points = initial.map(a => [a[0], a[1]]);
    phase = points.length > 0 && (next !== "polyline" || points.length >= 2) && (next !== "polygon" || points.length >= 3) ? "editing" : "drawing";
    history.reset(points);

    if (map.doubleClickZoom.enabled()) {
      map.doubleClickZoom.disable();
      doubleClickZoomWasEnabled = true;
    }

    tool = createTool(next);
    render();
    emitMode();
    emitChange();

    if ((options.fit ?? host.options.fit) && points.length > 0) {
      const bounds = L.latLngBounds(points);

      if (points.length === 1) map.setView(points[0], Math.max(map.getZoom(), 15), { animate: !host.reducedMotion });
      else if (bounds.isValid()) map.fitBounds(bounds, { padding: [48, 48], animate: !host.reducedMotion });
    }
  };

  const switchMode = (next: DrawMode) => {
    if (mode === null || mode === next) return;

    const kept = next === "point" ? points.slice(0, 1) : points;

    start(next, { points: kept, editable, fit: false });
  };

  const onMapClick = (event: L.LeafletMouseEvent) => {
    if (mode === null || tool === null || !editable) return;

    const original = event.originalEvent as MouseEvent;

    if (original.altKey) return;

    const snapped = snapper.snap([event.latlng.lat, event.latlng.lng], original.shiftKey);

    tool.onMapClick(snapped.point, original);
  };

  const onMapMouseMove = (event: L.LeafletMouseEvent) => {
    if (mode === null || tool === null || !editable) return;

    const original = event.originalEvent as MouseEvent;
    const snapped = phase === "drawing" ? snapper.snap([event.latlng.lat, event.latlng.lng], original.shiftKey) : { point: [event.latlng.lat, event.latlng.lng] as LatLngTuple };

    tool.onMapMouseMove(snapped.point, original);
  };

  const onMapDoubleClick = (event: L.LeafletMouseEvent) => {
    if (mode === null || tool === null || !editable) return;

    L.DomEvent.stop(event);
    tool.onDoubleClick();
  };

  const onMouseOut = () => {
    segment = null;
  };

  const undo = () => {
    const previous = history.undo();

    if (previous === null) return;

    setPoints(previous, false);
  };

  const redo = () => {
    const next = history.redo();

    if (next === null) return;

    setPoints(next, false);
  };

  const handleKey = (event: KeyboardEvent): boolean => {
    if (mode === null || isEditableTarget(event.target)) return false;

    if (event.key === "Escape") {
      event.preventDefault();
      cancel();
      return true;
    }

    if ((event.ctrlKey || event.metaKey) && (event.key === "z" || event.key === "Z")) {
      event.preventDefault();

      if (event.shiftKey) redo();
      else undo();

      return true;
    }

    if ((event.ctrlKey || event.metaKey) && (event.key === "y" || event.key === "Y")) {
      event.preventDefault();
      redo();
      return true;
    }

    if (!editable) return false;

    if (event.key === "Enter") {
      event.preventDefault();
      finish();
      return true;
    }

    if (event.key === "Backspace" && phase === "drawing") {
      event.preventDefault();
      tool?.removeLast();
      return true;
    }

    if (event.key === "Delete") {
      event.preventDefault();
      tool?.deleteSelectedVertex();
      return true;
    }

    if (!event.ctrlKey && !event.metaKey && !event.altKey && modeKeys[event.key] !== undefined) {
      event.preventDefault();
      switchMode(modeKeys[event.key]);
      return true;
    }

    return false;
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const inside = event.target instanceof Node && host.root.contains(event.target);

    if (!inside && event.target !== document.body && event.target !== document) return;

    handleKey(event);
  };

  map.on("click", onMapClick);
  map.on("mousemove", onMapMouseMove);
  map.on("dblclick", onMapDoubleClick);
  map.on("mouseout", onMouseOut);
  document.addEventListener("keydown", onKeyDown);

  const session: DrawSession = {
    start,
    mode: () => mode,
    getWkt: () => geometry().wkt,
    setWkt: wkt => {
      if (mode === null) return;

      setPoints(pointsFromWkt(wkt, mode), true);

      if (points.length > 0) phase = "editing";

      render();
    },
    setPoints: next => {
      if (mode === null) return;

      setPoints(mode === "point" ? next.slice(0, 1) : next, true);

      if (mode === "point" && points.length > 0) phase = "editing";

      render();
    },
    getGeometry: geometry,
    finish,
    cancel,
    clear: () => {
      if (mode === null) return;

      phase = "drawing";
      splitRemainder = null;
      selectedVertex = null;
      setPoints([], true);
    },
    undo,
    redo,
    canUndo: () => history.canUndo(),
    canRedo: () => history.canRedo(),
    setSnap: on => {
      snapper.enabled = on;

      if (!on) snapper.clearFlash();

      syncStore();
    },
    snap: () => snapper.enabled,
    setEditable: on => {
      editable = on;
      tool?.setEditable(on);
      render();
    },
    reverse: () => {
      if (mode === null || points.length < 2) return;

      setPoints(reversePoints(points), true);
    },
    simplify: toleranceMeters => {
      if (mode === null || points.length < 3) return;

      setPoints(simplifyPoints(points, toleranceMeters), true);
    },
    mergeWith: other => {
      if (mode !== "polyline" || other.length < 2) return;

      setPoints(mergeLines(points, other), true);
      phase = "editing";
      render();
    },
    deleteSelectedVertex: () => {
      tool?.deleteSelectedVertex();
    },
    dispose: () => {
      if (disposed) return;

      removeTool();
      map.off("click", onMapClick);
      map.off("mousemove", onMapMouseMove);
      map.off("dblclick", onMapDoubleClick);
      map.off("mouseout", onMouseOut);
      document.removeEventListener("keydown", onKeyDown);
      snapper.dispose();
      mode = null;
      points = [];
      history.reset([]);
      syncStore();
      disposed = true;
    },
    addSnapSource: source => snapper.addSource(source),
    active: () => mode !== null,
    phase: () => (mode === null ? null : phase),
    editable: () => editable,
    lastSplitRemainder: () => splitRemainder,
    previewPoints: next => toolHost.preview(next),
    commitPreview: () => {
      if (!previewActive) return;

      toolHost.commit(points);
    },
    selectedVertex: () => selectedVertex,
    segment: () => segment,
    handleKey
  };

  syncStore();

  if (host.options.mode !== null) {
    start(host.options.mode, {
      wkt: host.options.wkt,
      points: host.options.points.length > 0 ? host.options.points : undefined,
      editable: host.options.editable,
      fit: host.options.fit
    });
  }

  return session;
}
